import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Ticket, Users, Calendar, MapPin, Clock, ShoppingBag, Image as ImageIcon, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "@/components/EventCard";
import SquareBoard from "@/pages/SquareBoard";

const sideWords = ["Horses", "ATVs", "Trucks", "Bikes", "Good People"];

export default function Home() {
  const [c, setC] = useState({});
  const [ev, setEv] = useState(null);
  const [featuredMerch, setFeaturedMerch] = useState(null);
  const [galleryPics, setGalleryPics] = useState([]);
  const [eventPics, setEventPics] = useState([]);
  const [shopPics, setShopPics] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/content").then((r) => setC(r.data)).catch(() => {});
    api.get("/events/featured").then((r) => setEv(r.data && r.data.id ? r.data : null)).catch(() => {});
    api.get("/merch/featured").then((r) => setFeaturedMerch(r.data && r.data.id ? r.data : null)).catch(() => {});
    api.get("/gallery").then((r) => {
      setGalleryPics((r.data || []).filter((g) => !g.is_video && g.media_url).map((g) => g.media_url));
    }).catch(() => {});
    api.get("/events").then((r) => {
      const pics = [];
      (r.data || []).forEach((e) => { if (e.banner_image) pics.push(e.banner_image); if (e.flyer_image) pics.push(e.flyer_image); });
      setEventPics([...new Set(pics)]);
    }).catch(() => {});
    api.get("/merch").then((r) => {
      setShopPics([...new Set((r.data || []).filter((m) => m.image).map((m) => m.image))]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick((i) => i + 1), 6000);
    return () => clearInterval(t);
  }, []);

  const pick = (arr, fallback) => (arr.length ? arr[tick % arr.length] : fallback);
  const galleryImg = pick(galleryPics, c.card_gallery_image);
  const eventsImg = pick(eventPics, c.card_events_image);
  const shopImg = pick(shopPics, c.card_shop_image);

  const cards = [
    { title: "EVENTS", sub: "Trail rides, parties & more", to: "/events", img: eventsImg, icon: CalendarDays, rotating: eventPics.length > 1 },
    { title: "SHOP", sub: "Rep the movement", to: "/shop", img: shopImg, icon: ShoppingBag, rotating: shopPics.length > 1 },
    { title: "GALLERY", sub: "Good times. Great memories.", to: "/gallery", img: galleryImg, icon: ImageIcon, rotating: galleryPics.length > 1 },
  ];

  return (
    <div className="bg-[#0c0b0a]">
      {/* HERO */}
      <section className="relative min-h-[88vh] sm:min-h-[92vh] flex items-end overflow-hidden grain">
        <img src={c.hero_image || "/assets/hero_v2.jpg"} alt="I-65 Ryders" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0b0a] via-[#0c0b0a]/45 to-[#0c0b0a]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c0b0a]/85 via-transparent to-transparent" />

        {/* handwritten note */}
        <motion.p initial={{ opacity: 0, rotate: -8 }} animate={{ opacity: 1, rotate: -8 }} transition={{ delay: 0.5, duration: 0.8 }}
          className="hidden sm:block absolute top-24 right-8 lg:right-20 handwritten text-3xl lg:text-5xl text-[#f3c649] max-w-xs text-right drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
          {c.hero_note || "MORE THAN A RIDE\nIT'S A FAMILY."}
        </motion.p>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pb-14 sm:pb-20">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="eyebrow text-[#f7f3ec] text-base sm:text-xl mb-1 tracking-[0.35em]">
            {c.hero_welcome || "WELCOME TO"}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8 }} className="headline text-6xl sm:text-8xl lg:text-9xl gold-text drop-shadow-[0_4px_20px_rgba(0,0,0,0.6)]">
            {c.hero_headline || "I-65 RYDERS"}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }} className="headline text-xl sm:text-3xl text-[#f7f3ec] tracking-[0.15em] mt-2 mb-8">
            {c.hero_subheadline || "DIFFERENT TRAILS. SAME FAMILY."}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.8 }} className="flex flex-col sm:flex-row gap-3">
            <Link to="/events" className="btn-gold rounded-xl px-7 py-3.5 font-display uppercase tracking-wider text-base inline-flex items-center justify-center gap-2" data-testid="hero-view-events">
              <Calendar size={18} /> Upcoming Events
            </Link>
            <Link to="/events" className="rounded-xl px-7 py-3.5 font-display uppercase tracking-wider text-base inline-flex items-center justify-center gap-2 gold-border text-[#f7f3ec] bg-black/40 hover:bg-black/60 transition-colors" data-testid="hero-get-tickets">
              <Ticket size={18} /> Get Tickets
            </Link>
            <Link to="/join" className="rounded-xl px-7 py-3.5 font-display uppercase tracking-wider text-base inline-flex items-center justify-center gap-2 gold-border text-[#f7f3ec] bg-black/40 hover:bg-black/60 transition-colors" data-testid="hero-join">
              <Users size={18} /> Join The Ryders
            </Link>
          </motion.div>
        </div>
      </section>

      {/* SQUARE BOARD FUNDRAISER */}
      <section className="pt-10 sm:pt-14">
        <SquareBoard embedded />
      </section>

      {/* NEXT / FEATURED EVENT */}
      {ev && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="card-warm rounded-3xl overflow-hidden grid lg:grid-cols-[1.1fr_1.2fr_auto]">
            <div className="relative h-60 lg:h-auto min-h-[280px]">
              <img src={ev.banner_image || ev.flyer_image} alt={ev.title} className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <div className="p-7 sm:p-10">
              <p className="eyebrow text-[#d4af37] text-sm mb-2">Next Event</p>
              <h2 className="headline text-4xl sm:text-5xl text-[#f7f3ec] mb-6">{ev.title}</h2>
              <div className="space-y-3 mb-7 text-[#d6cec2]">
                <p className="flex items-center gap-3"><Calendar size={19} className="text-[#e0481f]" /> {formatDate(ev.date)}</p>
                <p className="flex items-center gap-3"><MapPin size={19} className="text-[#e0481f]" /> {ev.location}</p>
                {ev.event_time && <p className="flex items-center gap-3"><Clock size={19} className="text-[#e0481f]" /> {ev.event_time}</p>}
              </div>
              <Link to={`/events/${ev.slug}`} className="btn-gold rounded-xl w-full py-4 font-display uppercase tracking-wider text-lg flex items-center justify-center gap-2" data-testid="featured-get-tickets">
                <Ticket size={20} /> Get Tickets
              </Link>
            </div>
            <div className="hidden lg:flex flex-col justify-center gap-2 pr-10 pl-2 border-l border-[#d4af37]/15">
              {sideWords.map((w, i) => (
                <span key={w} className="handwritten text-3xl text-[#8a8078]" style={{ transform: `rotate(${i % 2 ? 3 : -3}deg)` }}>{w}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOUR CARDS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12 sm:pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div key={card.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to={card.to} className="group relative block h-72 sm:h-96 rounded-2xl overflow-hidden gold-border" data-testid={`home-card-${card.title.toLowerCase()}`}>
                  <img key={card.img} src={card.img} alt={card.title} className={`absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${card.rotating ? "animate-[fadeIn_1s_ease]" : ""}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute inset-0 p-5 flex flex-col justify-end items-center text-center">
                    <Icon size={28} className="text-[#f3c649] mb-2" />
                    <h3 className="headline text-2xl sm:text-3xl text-[#f7f3ec]">{card.title}</h3>
                    <p className="text-[#c9c0b4] text-xs sm:text-sm mb-4">{card.sub}</p>
                    <span className="w-10 h-10 rounded-full gold-border flex items-center justify-center text-[#f3c649] group-hover:bg-[#f3c649] group-hover:text-black transition-colors"><ArrowRight size={18} /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* BEST SELLER SPOTLIGHT */}
      {featuredMerch && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-12 sm:pb-16" data-testid="home-bestseller">
          <div className="card-warm rounded-3xl overflow-hidden grid md:grid-cols-2">
            <div className="relative min-h-[300px] md:min-h-[420px] bg-[#1a1816]">
              <img src={featuredMerch.image} alt={featuredMerch.name} className="absolute inset-0 w-full h-full object-cover" />
              <span className="absolute top-4 left-4 bg-[#e0481f] text-black text-xs font-display uppercase tracking-widest px-3 py-1.5 rounded-full">Best Seller</span>
            </div>
            <div className="p-8 sm:p-12 flex flex-col justify-center">
              <p className="eyebrow text-[#d4af37] text-sm mb-2">Fan Favorite</p>
              <h2 className="headline text-4xl sm:text-5xl text-[#f7f3ec] mb-3">{featuredMerch.name}</h2>
              {featuredMerch.description && <p className="text-[#d6cec2] text-lg leading-relaxed mb-4">{featuredMerch.description}</p>}
              <p className="headline text-4xl gold-text mb-7">${featuredMerch.price}</p>
              <Link to="/shop" className="btn-gold rounded-xl px-8 py-4 font-display uppercase tracking-wider text-lg inline-flex items-center gap-3 w-fit" data-testid="bestseller-shop">
                <ShoppingBag size={20} /> Shop Now
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* MEMBERSHIP BANNER */}
      <section className="relative">
        <div className="grid lg:grid-cols-2">
          <div className="relative h-72 lg:h-auto min-h-[380px]">
            <img src={c.membership_image || "/assets/membership_jacket.jpg"} alt="I-65 Ryders member" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0c0b0a] lg:to-[#0c0b0a] hidden lg:block" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c0b0a] to-transparent lg:hidden" />
          </div>
          <div className="bg-[#0c0b0a] flex items-center px-6 sm:px-12 py-14 lg:py-24">
            <div className="max-w-xl">
              <p className="eyebrow text-[#d4af37] text-sm mb-3">{c.join_eyebrow || "BE MORE THAN A FOLLOWER"}</p>
              <h2 className="headline text-4xl sm:text-6xl text-[#f7f3ec] mb-5">{c.join_headline || "BECOME A RYDER"}</h2>
              <p className="text-[#d6cec2] text-lg leading-relaxed mb-8">{c.join_text || "A community built on riding, good people, and great times. Different trails, same family."}</p>
              <Link to="/join" className="btn-gold rounded-xl px-8 py-4 font-display uppercase tracking-wider text-lg inline-flex items-center gap-3" data-testid="membership-apply">
                Apply Now <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
